import {Component, Inject, OnInit}                                                    from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef}                                                from '@angular/material/dialog';
import {MatSnackBar}                                                                  from '@angular/material/snack-bar';
import {FormOptionGroupService}                                                       from '@src/app/shared/services/form-option-group.service';
import {FormOptionGroup}                                                              from '@src/app/shared/models/option-group.model';

@Component({
    standalone: false,
    selector: 'app-option-group-form',
    templateUrl: './option-group-form.component.html',
})
export class OptionGroupFormComponent implements OnInit
{
    public title = 'Create Option Group';
    public group: FormOptionGroup | null = null;

    public groupForm: UntypedFormGroup = new UntypedFormGroup({});

    public constructor(
        @Inject(MAT_DIALOG_DATA) public data: { group: FormOptionGroup | null },
        private optionGroupService: FormOptionGroupService,
        private formBuilder: UntypedFormBuilder,
        private snackBar: MatSnackBar,
        public dialogRef: MatDialogRef<OptionGroupFormComponent>,
    )
    {
    }

    public ngOnInit(): void
    {
        this.group = this.data.group;

        if (this.group) {
            this.title = `Edit Option Group — ${this.group.name}`;
        }

        this.groupForm = this.formBuilder.group({
            name: new UntypedFormControl(this.group?.name ?? '', Validators.required),
            options: this.formBuilder.array(
                (this.group?.options?.length
                    ? [...this.group.options].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    : [{label: '', value: ''}]
                ).map(option => this.buildOptionRow(option.label ?? '', option.value ?? ''))
            ),
        });
    }

    public get optionRows(): UntypedFormArray
    {
        return this.groupForm.get('options') as UntypedFormArray;
    }

    public addOptionRow(): void
    {
        this.optionRows.push(this.buildOptionRow('', ''));
    }

    public removeOptionRow(index: number): void
    {
        this.optionRows.removeAt(index);
    }

    public moveOptionRow(index: number, direction: -1 | 1): void
    {
        const target = index + direction;
        if (target < 0 || target >= this.optionRows.length) {
            return;
        }

        const row = this.optionRows.at(index);
        this.optionRows.removeAt(index);
        this.optionRows.insert(target, row);
    }

    public submitForm(): void
    {
        if (this.groupForm.invalid) {
            this.groupForm.markAllAsTouched();
            return;
        }

        const options = (this.optionRows.value as Array<{ label: string; value: string }>)
            .filter(option => (option.label ?? '').trim().length || (option.value ?? '').trim().length)
            .map((option, index) => ({label: option.label.trim(), value: option.value.trim(), order: index}));

        if (!options.length) {
            this.snackBar.open('Add at least one option before saving.', 'Ok', {duration: 3000});
            return;
        }

        const payload = {
            name: this.groupForm.get('name')?.value,
            reusable: true,
            options,
        };

        const request = this.group
            ? this.optionGroupService.updateOptionGroup(this.group.id, payload)
            : this.optionGroupService.createOptionGroup(payload);

        request.subscribe({
            next: response => this.dialogRef.close(response),
            error: response => {
                const message = response?.error?.message ?? 'Failed to save option group.';
                this.snackBar.open(message, 'Ok', {duration: 4000});
            }
        });
    }

    public closeDialog(): void
    {
        this.dialogRef.close();
    }

    private buildOptionRow(label: string, value: string): UntypedFormGroup
    {
        return this.formBuilder.group({
            label: new UntypedFormControl(label, Validators.required),
            value: new UntypedFormControl(value, Validators.required),
        });
    }
}
